import React, { useContext } from 'react';
import { actions, StateContext } from '../../state/index';
import {
  AutoAcceptFlow,
  BasicAcceptFlow,
  SelectCurrencyFlow,
  FinixAcceptFlow,
} from './flows/index';
import {
  BaseAcceptView,
  FinixAcceptView,
  AddressAcceptView,
  ACHAcceptView,
} from './views/index';
import { logger } from '../../utils/logging';
import { LOG_LEVELS, LOGGING, TOKEN_TYPES } from '../../utils/enums';
import { postAcceptPayment } from '../../api/index';
import {
  BROWSER_STORAGE_ITEMS,
  PAYMENT_STATUSES,
  FLOW_TYPES,
  PAYMENT_NETWORKS,
  HTTP_REQUEST_TYPES,
} from '../../utils/enums';
import { HttpRequestException } from '../../utils/errors';
import { logHttpError } from '../../utils/logging';

const AcceptPaymentFlows = () => {
  const [applicationState, dispatch] = useContext(StateContext);
  const { configuration, payment, tokens, selectedPaymentNetwork } =
    applicationState;

  const acceptPayment = async (
    additionalDataForPaymentAcceptance,
    additionalDataForLogs
  ) => {
    const baselineDataForLogs = {
      programId: payment.programId,
      programName: configuration.programName,
      acceptanceActionType: 'manual',
    };

    try {
      const baselineDataForAcceptPayment = {
        acceptedAmount: payment.amount,
        acceptedCurrency: payment.currency,
      };

      const dataForAcceptPayment = {
        ...baselineDataForAcceptPayment,
        ...additionalDataForPaymentAcceptance,
      };

      logger({
        event: LOGGING.EVENTS.PAYMENT_ACCEPTANCE_ATTEMPTED,
        paymentNetwork: selectedPaymentNetwork,
        ...baselineDataForAcceptPayment,
        ...baselineDataForLogs,
        ...additionalDataForLogs,
      });

      const response = await postAcceptPayment(
        payment.id,
        tokens[TOKEN_TYPES.RECIPIENT_LOGIN].access_token,
        dataForAcceptPayment
      );
      window.sessionStorage.setItem(
        BROWSER_STORAGE_ITEMS.INITIAL_COMPLETE,
        true
      );
      const fetchedPayment = response.data;

      logger({
        event: LOGGING.EVENTS.PAYMENT_ACCEPTANCE_RESULT,
        paymentStatus: fetchedPayment.status,
        paymentNetwork: fetchedPayment.paymentNetwork,
        acceptedAmount: fetchedPayment.acceptedAmount,
        acceptedCurrency: fetchedPayment.acceptedCurrency,
        ...baselineDataForLogs,
        ...additionalDataForLogs,
      });

      // trigger polling if payment is not yet complete
      dispatch(
        actions.payment.setPayment({
          ...fetchedPayment,
          status:
            [PAYMENT_STATUSES.COMPLETE, PAYMENT_STATUSES.ACCEPTED].indexOf(
              fetchedPayment.status
            ) === -1
              ? PAYMENT_STATUSES.POLLING
              : fetchedPayment.status,
        })
      );
    } catch (error) {
      const httpRequestException = HttpRequestException(
        HTTP_REQUEST_TYPES.ACCEPT_PAYMENT,
        error
      );

      logHttpError(httpRequestException, applicationState);
      logger(
        {
          event: LOGGING.EVENTS.PAYMENT_ACCEPTANCE_ERROR,
          paymentNetwork: selectedPaymentNetwork,
          ...baselineDataForLogs,
          error,
        },
        LOG_LEVELS.ERROR
      );

      dispatch(
        actions.modals.showHttpErrorModal(configuration, {
          errorType: httpRequestException.errorType,
          serverResponse: httpRequestException.serverResponse,
          propsByHttpStatus: {
            default: {
              showChooseAnotherNetwork: false,
              actions: [
                {
                  label: 'Start Over',
                  onClick: () => dispatch(actions.startOver()),
                },
              ],
            },
            401: {
              showFaqLink: false,
            },
          },
        })
      );

      return Promise.reject(error);
    }
  };

  let Flow;
  let View;

  switch (selectedPaymentNetwork) {
    case PAYMENT_NETWORKS.OCT_FINIX:
      Flow = FinixAcceptFlow;
      View = FinixAcceptView;
      break;
    default:
      View = BaseAcceptView;
      break;
  }

  switch (configuration.acceptPayment.flow) {
    case FLOW_TYPES.ACCEPT_PAYMENT.AUTO_ACCEPT:
      Flow = AutoAcceptFlow;
      break;
    case FLOW_TYPES.ACCEPT_PAYMENT.SELECT_CURRENCY:
      Flow = SelectCurrencyFlow;
      break;
    case FLOW_TYPES.ACCEPT_PAYMENT.COLLECT_ADDRESS:
      Flow = BasicAcceptFlow;
      View = AddressAcceptView;
      break;
    case FLOW_TYPES.ACCEPT_PAYMENT.CONFIRM_ACH_DETAILS:
      Flow = BasicAcceptFlow;
      View = ACHAcceptView;
      break;
    case FLOW_TYPES.ACCEPT_PAYMENT.BASIC:
    default:
      Flow = BasicAcceptFlow;
      break;
  }

  return <Flow acceptPayment={acceptPayment}>{View}</Flow>;
};

export default AcceptPaymentFlows;
